package features.schedule.data

import kotlin.js.Promise
import core.firebase.FirestoreClient
import features.schedule.domain.Schedule

/**
 * Repository for schedule database operations
 */
object ScheduleRepository {
    private val db get() = FirestoreClient.db
    
    fun loadSchedule(teamId: String, year: Int, month: Int): Promise<Schedule?> {
        val scheduleId = "$year-${month.toString().padStart(2, '0')}"
        
        return db.collection("teams").doc(teamId)
            .collection("schedules").doc(scheduleId).get()
            .then { doc ->
                if (doc.exists) {
                    val data = doc.data()
                    if (data != null) {
                        val assignmentsData = data.asDynamic().assignments
                        val assignments = mutableMapOf<String, MutableMap<Int, String>>()
                        
                        // Convert JS object to Kotlin map
                        val memberIds: Array<String> = js("Object.keys(assignmentsData)")
                        memberIds.forEach { memberId ->
                            val memberAssignments = mutableMapOf<Int, String>()
                            val dayKeys: Array<String> = js("Object.keys(assignmentsData[memberId])")
                            dayKeys.forEach { dayStr ->
                                val day = dayStr.toInt()
                                val shiftCode: String = js("assignmentsData[memberId][dayStr]")
                                memberAssignments[day] = shiftCode
                            }
                            assignments[memberId] = memberAssignments
                        }
                        
                        Schedule(month, year, assignments)
                    } else null
                } else null
            }
    }
    
    fun assignShift(teamId: String, year: Int, month: Int, memberId: String, day: Int, shiftCode: String): Promise<Unit> {
        val scheduleId = "$year-${month.toString().padStart(2, '0')}"
        
        return db.collection("teams").doc(teamId)
            .collection("schedules").doc(scheduleId).get()
            .then { doc ->
                val assignments = if (doc.exists) {
                    doc.data()?.asDynamic()?.assignments ?: js("{}")
                } else {
                    js("{}")
                }
                
                // Ensure member exists in assignments
                if (assignments[memberId] == null) {
                    assignments[memberId] = js("{}")
                }
                
                // Set the shift for the day
                assignments[memberId][day] = shiftCode
                
                // Save back to Firestore
                val scheduleData = js("""({
                    month: month,
                    year: year,
                    assignments: assignments
                })""")
                
                db.collection("teams").doc(teamId)
                    .collection("schedules").doc(scheduleId)
                    .set(scheduleData, js("{ merge: true }"))
            }
    }
    
    fun saveSchedule(teamId: String, schedule: Schedule): Promise<Unit> {
        val docId = "${schedule.year}-${schedule.month.toString().padStart(2, '0')}"
        
        // Manually create JS object to avoid undefined fields
        val scheduleData = js("{}")
        scheduleData["year"] = schedule.year
        scheduleData["month"] = schedule.month
        
        // Convert assignments map to plain JS object
        val assignmentsObj = js("{}")
        schedule.assignments.forEach { (memberId, days) ->
            val daysObj = js("{}")
            days.forEach { (day, shiftCode) ->
                daysObj[day.toString()] = shiftCode
            }
            assignmentsObj[memberId] = daysObj
        }
        scheduleData["assignments"] = assignmentsObj
        
        console.log("Saving schedule:", scheduleData)
        
        return db.collection("teams").doc(teamId)
            .collection("schedules").doc(docId).set(scheduleData)
    }
}
